import {
  useState,
  MouseEvent,
  FC,
  Dispatch,
  useEffect,
  useRef,
  ChangeEvent,
} from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import SearchBar from "@/components/SearchBar";
import Text from "@/components/Text";
import { Tags } from "@/pages/AssignTopics/components/AttachmentsField";
import Tag from "@/components/Tag";
import { VideoTopicResource } from "@/types/resources";
import { api } from "@/utils/api";
import useDebounce from "@/hooks/useDebounce";
import { Inner, Item } from "@/components/Dropdown";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { TopicsLevel } from "@/utils/enums";
import { Spinner } from "@/components/Button";

const Box = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  margin-bottom: 32px;
`;

const BoxTop = styled.div`
  padding: 16px 24px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const BoxBottom = styled.div`
  padding: 16px 24px 24px;
`;

const BoxTopRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const BoxTopLeft = styled.div`
  width: 264px;
  position: relative;
`;

const TopicsList = styled.div`
  padding: 0 8px 12px;
`;

const BoxTopRight = styled.div`
  flex: 1;
  padding-left: 16px;
`;

const Title = styled(Text)`
  margin-bottom: 8px;
`;

const StyledSearchBar = styled(SearchBar)`
  margin-bottom: 0;
`;

const SearchHolder = styled.div`
  position: relative;
`;

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: calc(50% - 9px);
  right: 0;
  margin-right: 16px;
  color: ${({ theme }) => theme.colors.purple};
`;

const TitleText = styled(Text)`
  margin: 12px 14px 8px;
`;

const Capital = styled.span`
  text-transform: capitalize;
`;

export const NoTopics = styled.div`
  text-align: center;
  margin-top: 24px;
  margin-bottom: 24px;
  padding: 0 12px;
`;

const StyledInner = styled(Inner)`
  position: absolute;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: 3;
  width: 100%;
  left: 0;
  top: calc(100% + 8px);
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 0;
`;

const StyledTags = styled(Tags)`
  margin: 0;
`;

const Level = styled.span`
  font-weight: 700;
`;

const StyledTag = styled(Tag)`
  font-size: 11px;
`;

const StyledItem = styled(Item)`
  text-align: left;
  display: block;
  padding: 5px 14px;
  ${Text} {
    transition: color ease-out 0.2s;
    &:first-child {
      margin-bottom: 2px;
      span {
        color: ${({ theme }) => theme.colors.purple};
      }
    }
  }
  &:hover {
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
`;

const CheckboxText = styled(Text)`
  color: ${({ theme }) => theme.colors.gray};
  transition: color ease-out 0.2s;
`;

const CheckboxLabel = styled.div`
  position: relative;
  display: block;
  height: 16px;
  margin-right: 9px;
  width: 16px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  background-color: ${({ theme }) => theme.colors.white};
  transition: border-color ease-out 0.2s;
`;

const CheckboxLabelHolder = styled.label`
  display: flex;
  cursor: pointer;
  &:hover {
    ${CheckboxLabel} {
      border-color: ${({ theme }) => theme.colors.purpleLighter};
    }
    ${CheckboxText} {
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

const Checkbox = styled.input`
  display: none;
  &:checked + ${CheckboxLabelHolder} {
    > {
      ${CheckboxText} {
        color: ${({ theme }) => theme.colors.black};
      }
    }
    > ${CheckboxLabel} {
      background: ${({ theme }) => theme.colors.purple};
      border-color: transparent;
      &:after {
        content: "";
        position: absolute;
        left: 4px;
        top: 1px;
        width: 4px;
        height: 8px;
        border: solid ${({ theme }) => theme.colors.white};
        border-width: 0 2px 2px 0;
        -webkit-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        transform: rotate(45deg);
      }
    }
  }
`;

const NoTopicsTitle = styled(Text)`
  margin-bottom: 8px;
`;

const Checkboxes = styled.div`
  margin-top: 16px;
  display: flex;
`;

const CheckboxGroup = styled.div`
  margin-right: 34px;
`;

const renderTopicLevelTitle = (level: TopicsLevel) => {
  switch (level) {
    case TopicsLevel.GENRE:
      return "Genre";
    case TopicsLevel.SUBGENRE:
      return "Sub-genre";
    case TopicsLevel.LOCATIONS:
      return "Location";
    case TopicsLevel.OBJECTS:
      return "Object";
    case TopicsLevel.CHARACTERS:
      return "Character";
    default:
      return "";
  }
};

interface Props {
  category?: string;
  topics: VideoTopicResource[];
  setTopics: Dispatch<React.SetStateAction<VideoTopicResource[]>>;
  setSelectedCombos: Dispatch<React.SetStateAction<TopicsLevel[]>>;
}

const TopScriptTopicsLevelsFilter: FC<Props> = ({
  setTopics,
  topics,
  category,
  setSelectedCombos,
}) => {
  const { teamId } = useParams();

  const [isLoading, setIsLoading] = useState(false);

  const [showDropdown, setShowDropdown] = useState(false);

  const [search, setSearch] = useState("");

  const [suggestions, setSuggestions] = useState<VideoTopicResource[]>([]);

  const debouncedTopic = useDebounce(search, 400);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowDropdown(false));

  const splitMatchedText = (str: string) => {
    const regExp = new RegExp(debouncedTopic, "gi");
    return str.replace(regExp, (match) => `<span>${match}</span>`);
  };

  useEffect(() => {
    if (debouncedTopic.length < 2) {
      setIsLoading(false);
      return setSuggestions([]);
    }

    setIsLoading(true);

    api
      .get<never, VideoTopicResource[]>("/v1/video-topics/search", {
        params: {
          ...(!!category && { "filter[category]": category }),
          "filter[search]": debouncedTopic,
          ...(teamId !== "all" && {
            "filter[teamId]": teamId,
          }),
        },
      })
      .then((suggestions) => {
        setSuggestions(suggestions);
        setIsLoading(false);
      });
  }, [debouncedTopic, category, teamId]);

  const onTopicRemove = (
    e: MouseEvent<HTMLButtonElement>,
    topicToRemove: VideoTopicResource
  ) => {
    e.stopPropagation();
    setTopics((prevTopics) =>
      prevTopics.filter((topic) => topic.id !== topicToRemove.id)
    );
  };

  const onTopicAdd = (
    e: MouseEvent<HTMLButtonElement>,
    topicToAdd: VideoTopicResource
  ) => {
    e.stopPropagation();
    setTopics((prevTopics) => [...prevTopics, topicToAdd]);
  };

  const noSuggestionsFound =
    debouncedTopic.length >= 2 && !!!suggestions.length && !isLoading;

  const filteredSuggestions = suggestions.filter(
    (suggestion) => !topics.map((t) => t.id).includes(suggestion.id)
  );

  return (
    <>
      <Box>
        <BoxTop>
          <Title $color="black" $type="smallBold">
            Topics:
          </Title>
          <BoxTopRow>
            <BoxTopLeft ref={ref}>
              <SearchHolder onClick={() => setShowDropdown(true)}>
                <StyledSearchBar value={search} setValue={setSearch} />
                {isLoading && <StyledSpinner />}
              </SearchHolder>
              {showDropdown && (
                <StyledInner>
                  {noSuggestionsFound && (
                    <NoTopics>
                      <NoTopicsTitle $color="black" $type="smallBold">
                        No topics found
                      </NoTopicsTitle>
                      <Text $color="gray" $type="smallMedium">
                        Topic may not been added to the system library.
                      </Text>
                    </NoTopics>
                  )}
                  {!!filteredSuggestions.length && (
                    <TopicsList>
                      <TitleText $type="extraSmall">
                        Keyword suggestions
                      </TitleText>
                      {filteredSuggestions.map((suggestion) => (
                        <StyledItem
                          key={`${suggestion.id}-${suggestion.level}`}
                          as="button"
                          onClick={(e: MouseEvent<HTMLButtonElement>) =>
                            onTopicAdd(e, suggestion)
                          }
                        >
                          <Text
                            dangerouslySetInnerHTML={{
                              __html: splitMatchedText(suggestion.title),
                            }}
                            $color="gray"
                            $type="smallBold"
                          />
                          <Text $color="gray" $type="extraSmall">
                            <Capital>
                              {renderTopicLevelTitle(suggestion.level)}
                            </Capital>{" "}
                            topic
                          </Text>
                        </StyledItem>
                      ))}
                    </TopicsList>
                  )}
                </StyledInner>
              )}
            </BoxTopLeft>
            <BoxTopRight>
              {!!topics.length && (
                <StyledTags>
                  {topics.map((topic) => {
                    return (
                      <StyledTag
                        key={topic.id}
                        onDelete={(e) => onTopicRemove(e, topic)}
                      >
                        <Level>{renderTopicLevelTitle(topic.level)}:</Level>
                        &nbsp;{topic.title}
                      </StyledTag>
                    );
                  })}
                </StyledTags>
              )}
            </BoxTopRight>
          </BoxTopRow>
        </BoxTop>
        <BoxBottom>
          <Title $color="black" $type="smallBold">
            Combos:
          </Title>
          <Checkboxes>
            <CheckboxGroup>
              <Checkbox
                id={`genre-checkbox`}
                name="thumbnails"
                type="checkbox"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    return setSelectedCombos((prev) => [
                      ...prev,
                      TopicsLevel.GENRE,
                    ]);
                  }
                  setSelectedCombos((prev) =>
                    prev.filter((element) => element !== TopicsLevel.GENRE)
                  );
                }}
              />
              <CheckboxLabelHolder htmlFor={`genre-checkbox`}>
                <CheckboxLabel />
                <CheckboxText $type="smallHeadline">Genre tags</CheckboxText>
              </CheckboxLabelHolder>
            </CheckboxGroup>
            <CheckboxGroup>
              <Checkbox
                id={`sub-genre-checkbox`}
                name="thumbnails"
                type="checkbox"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    return setSelectedCombos((prev) => [
                      ...prev,
                      TopicsLevel.SUBGENRE,
                    ]);
                  }
                  setSelectedCombos((prev) =>
                    prev.filter((element) => element !== TopicsLevel.SUBGENRE)
                  );
                }}
              />
              <CheckboxLabelHolder htmlFor={`sub-genre-checkbox`}>
                <CheckboxLabel />
                <CheckboxText $type="smallHeadline">
                  Sub-genre tags
                </CheckboxText>
              </CheckboxLabelHolder>
            </CheckboxGroup>
            <CheckboxGroup>
              <Checkbox
                id={`location-checkbox`}
                name="thumbnails"
                type="checkbox"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    return setSelectedCombos((prev) => [
                      ...prev,
                      TopicsLevel.LOCATIONS,
                    ]);
                  }
                  setSelectedCombos((prev) =>
                    prev.filter((element) => element !== TopicsLevel.LOCATIONS)
                  );
                }}
              />
              <CheckboxLabelHolder htmlFor={`location-checkbox`}>
                <CheckboxLabel />
                <CheckboxText $type="smallHeadline">Location tags</CheckboxText>
              </CheckboxLabelHolder>
            </CheckboxGroup>
            <CheckboxGroup>
              <Checkbox
                id={`character-checkbox`}
                name="thumbnails"
                type="checkbox"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    return setSelectedCombos((prev) => [
                      ...prev,
                      TopicsLevel.CHARACTERS,
                    ]);
                  }
                  setSelectedCombos((prev) =>
                    prev.filter((element) => element !== TopicsLevel.CHARACTERS)
                  );
                }}
              />
              <CheckboxLabelHolder htmlFor={`character-checkbox`}>
                <CheckboxLabel />
                <CheckboxText $type="smallHeadline">
                  Character tags
                </CheckboxText>
              </CheckboxLabelHolder>
            </CheckboxGroup>
            <CheckboxGroup>
              <Checkbox
                id={`object-checkbox`}
                name="thumbnails"
                type="checkbox"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    return setSelectedCombos((prev) => [
                      ...prev,
                      TopicsLevel.OBJECTS,
                    ]);
                  }
                  setSelectedCombos((prev) =>
                    prev.filter((element) => element !== TopicsLevel.OBJECTS)
                  );
                }}
              />
              <CheckboxLabelHolder htmlFor={`object-checkbox`}>
                <CheckboxLabel />
                <CheckboxText $type="smallHeadline">Object tags</CheckboxText>
              </CheckboxLabelHolder>
            </CheckboxGroup>
          </Checkboxes>
        </BoxBottom>
      </Box>
    </>
  );
};

export default TopScriptTopicsLevelsFilter;
